/* eslint-disable no-console */

import { register } from "register-service-worker";

// Type declarations
declare global {
  interface Window {
    updateManager: UpdateManager;
    swRegistration: ServiceWorkerRegistration;
  }
}

// Global update manager
class UpdateManager {
  public waitingWorker: ServiceWorker | null;
  public showUpdateAvailable: boolean;

  constructor() {
    this.waitingWorker = null;
    this.showUpdateAvailable = false;
  }

  // Show update notification to user
  notifyUpdateAvailable() {
    console.log("[UpdateManager] notifyUpdateAvailable called");
    this.showUpdateAvailable = true;
    console.log("[UpdateManager] showUpdateAvailable set to:", this.showUpdateAvailable);

    // Dispatch custom event for components to listen to
    const updateEvent = new CustomEvent("sw-update-available", {
      detail: {
        skipWaiting: () => this.skipWaiting(),
      },
    });
    console.log("[UpdateManager] Dispatching sw-update-available event:", updateEvent);
    window.dispatchEvent(updateEvent);

    // Also set the sessionStorage flag for backward compatibility
    sessionStorage.setItem("cw.vue.newVersion", "true");
    sessionStorage.setItem("cw.vue.updatePrompted", "false");
    console.log("[UpdateManager] SessionStorage updated - newVersion: true, updatePrompted: false");
  }

  // Apply the update immediately
  skipWaiting() {
    console.log("skipWaiting called");

    // Set a flag to prevent re-showing the banner immediately after reload
    sessionStorage.setItem("cw.vue.updateInProgress", "true");

    // Clear the session storage flags immediately
    sessionStorage.removeItem("cw.vue.newVersion");
    sessionStorage.removeItem("cw.vue.updatePrompted");

    // For Vue CLI PWA, the best approach is to just reload
    // The service worker will automatically activate the new version
    console.log("Reloading page to activate new service worker");
    window.location.reload();
  }

  // Check for updates manually
  checkForUpdates() {
    if ("serviceWorker" in navigator) {
      navigator.serviceWorker.ready.then((registration) => {
        console.log("Manual update check triggered");
        registration
          .update()
          .then(() => {
            console.log("Manual update check completed");
          })
          .catch((err) => {
            console.error("Manual update check failed:", err);
          });
      });
    }
  }

  // Test function to simulate update found
  simulateUpdate() {
    console.log("Simulating update found...");
    this.notifyUpdateAvailable();
  }
}

// Create global instance
window.updateManager = new UpdateManager();

if (process.env.NODE_ENV === "production") {
  register(`${process.env.BASE_URL}service-worker.js`, {
    ready() {
      console.log("App is being served from cache by a service worker.\n" + "For more details, visit https://goo.gl/AFskqB");
    },
    registered(registration) {
      console.log("Service worker has been registered.");
      console.log("Registration:", registration);
      console.log("Active worker:", registration.active);
      console.log("Waiting worker:", registration.waiting);

      // Store registration globally for manual testing
      window.swRegistration = registration;

      // Check for updates every 60 seconds
      setInterval(() => {
        console.log("Checking for updates automatically...");
        registration
          .update()
          .then(() => {
            console.log("Update check completed");
          })
          .catch((err) => {
            console.error("Update check failed:", err);
          });
      }, 60000);

      // Note: Don't automatically check for waiting workers here
      // as it can cause duplicate notifications after updates
    },
    cached() {
      console.log("Content has been cached for offline use.");
    },
    updatefound() {
      console.log("New content is downloading.");
    },
    updated(registration) {
      console.log("[SW] New content is available; please refresh.");
      console.log("[SW] Registration waiting:", registration.waiting);
      console.log("[SW] UpdateManager exists:", !!window.updateManager);
      console.log("[SW] Current location:", window.location.href);

      // Check if we just did an update to avoid immediate re-notification
      const updateInProgress = sessionStorage.getItem("cw.vue.updateInProgress") === "true";
      if (updateInProgress) {
        console.log("[SW] Update in progress flag detected, skipping notification");
        return;
      }

      // Store reference to the waiting service worker
      window.updateManager.waitingWorker = registration.waiting;

      // Notify user about available update
      console.log("[SW] Calling notifyUpdateAvailable...");
      window.updateManager.notifyUpdateAvailable();
    },
    offline() {
      console.log("No internet connection found. App is running in offline mode.");
    },
    error(error) {
      console.error("Error during service worker registration:", error);
    },
  });

  // Listen for controlling service worker change
  if ("serviceWorker" in navigator) {
    navigator.serviceWorker.addEventListener("controllerchange", () => {
      console.log("Service worker controller changed - reloading page");
      window.location.reload();
    });

    // Add debugging for service worker state changes
    navigator.serviceWorker.ready.then((registration) => {
      console.log("Service worker ready, setting up state monitoring");

      // Monitor the active worker
      if (registration.active) {
        console.log("Active worker state:", registration.active.state);
        registration.active.addEventListener("statechange", () => {
          console.log("Active worker state changed to:", registration.active?.state);
        });
      }

      // Monitor for new workers
      registration.addEventListener("updatefound", () => {
        console.log("Update found via registration listener!");
        const newWorker = registration.installing;
        if (newWorker) {
          console.log("New worker installing, state:", newWorker.state);
          newWorker.addEventListener("statechange", () => {
            console.log("New worker state changed to:", newWorker.state);
            if (newWorker.state === "installed") {
              if (navigator.serviceWorker.controller) {
                console.log("New service worker installed and ready to activate");
                // This is where we should trigger the update notification
                window.updateManager.waitingWorker = newWorker;
                window.updateManager.notifyUpdateAvailable();
              } else {
                console.log("Service worker installed for the first time");
              }
            } else if (newWorker.state === "activated") {
              // If we missed the "installed" state, trigger here
              if (navigator.serviceWorker.controller !== newWorker) {
                console.log("New service worker activated - showing update notification");
                window.updateManager.waitingWorker = newWorker;
                window.updateManager.notifyUpdateAvailable();
              }
            }
          });
        }
      });
    });
  }
}
